/**
 * Which client page an adviser lands on.
 *
 * V1 (`/client`) and V2 (`/clientv2`) run side by side for as long as it takes:
 * both routes stay mounted, both stay fully functional, and NOTHING here
 * changes where an adviser goes unless they opt in themselves. The stored
 * preference defaults to V1, so `clientPath()` returns `/client` for every
 * adviser who never presses 試用新版客戶頁 — the behaviour they have today,
 * byte for byte.
 *
 * The preference lives in localStorage rather than the store because the client
 * page is reached from ~45 places, several of which navigate before any React
 * tree that could read Redux has mounted.
 */
export const CLIENT_V1_PATH = '/client';
export const CLIENT_V2_PATH = '/clientv2';
export const CLIENT_PATHS = [CLIENT_V1_PATH, CLIENT_V2_PATH];

export const CLIENT_PAGE_VERSION_KEY = 'clientPageVersion';

export type ClientPageVersion = 'v1' | 'v2';

/**
 * True for either client page. Use this for every "am I on the client page"
 * check in the app chrome (footer tab highlight, top-nav icons, menu button),
 * so V2 gets the same chrome V1 has.
 */
export const isClientPath = (pathname?: string | null): boolean =>
  typeof pathname === 'string' && CLIENT_PATHS.includes(pathname);

/** The adviser's own choice. Anything unrecognised means V1. */
export const clientPageVersion = (): ClientPageVersion => {
  try {
    return localStorage.getItem(CLIENT_PAGE_VERSION_KEY) === 'v2' ? 'v2' : 'v1';
  } catch (error) {
    // Private mode / disabled storage: fall back to the untouched behaviour.
    return 'v1';
  }
};

/**
 * Set from V1's 試用新版客戶頁 button and cleared from V2's 回到舊版 control.
 * Writing 'v1' removes the key rather than storing it, so an adviser who opts
 * out is indistinguishable from one who never opted in.
 */
export const setClientPageVersion = (version: ClientPageVersion): void => {
  try {
    if (version === 'v2') localStorage.setItem(CLIENT_PAGE_VERSION_KEY, 'v2');
    else localStorage.removeItem(CLIENT_PAGE_VERSION_KEY);
  } catch (error) {
    // Not being able to remember the choice is not worth breaking navigation.
  }
};

/** The client page this adviser should be sent to. */
export const clientPath = (): string => (clientPageVersion() === 'v2' ? CLIENT_V2_PATH : CLIENT_V1_PATH);
